import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';
import type { 
  WorkflowAssignmentStatus, 
  WorkflowPriority 
} from './threeCollectionWorkflow';

/**
 * ENHANCED WORKFLOW TYPES
 * 
 * This file contains the enhanced data model for the Workflow Allocation System,
 * extending existing workflow structures to support workflow spot creation,
 * configuration, and approval workflows while maintaining backward compatibility.
 * 
 * Based on: docs/workflows/allocation/data-model-specification.md
 */

// ============================================================================
// CORE ENHANCED TYPES
// ============================================================================

/**
 * Complexity levels for workflows and workflow spots
 */
export type ComplexityLevel = 'high' | 'medium' | 'low';

/**
 * Team experience categories
 */
export type TeamExperience = 'Mixed' | 'Social' | 'Search' | 'Programmatic';

/**
 * Project style preferences
 */
export type ProjectStyle = 'Ecommerce' | 'B2B Lead Gen' | 'User Acquisition' | 'Any';

/**
 * Decision drivers for workflow spot creation
 */
export type DecisionDriver = 'New Hire' | 'Project Churn' | 'Change in Workflow' | 'Capacity Identification';

/**
 * Approval status for workflow spots
 * - draft: Being configured, not yet submitted
 * - pending_approval: Submitted and awaiting director review
 * - approved: Approved and ready for assignment
 * - deferred: Needs revision before approval
 */
export type ApprovalStatus = 'draft' | 'pending_approval' | 'approved' | 'deferred';

/**
 * Conversation message types
 */
export type ConversationMessageType = 'comment' | 'status_change' | 'approval_request';

// ============================================================================
// CONVERSATION SYSTEM
// ============================================================================

/**
 * Individual conversation entry
 */
export interface ConversationEntry {
  id: string;
  author_id: string;
  author_name: string;
  author_avatar_url?: string;
  message: string;
  timestamp: Timestamp;
  message_type: ConversationMessageType;
  metadata?: {
    previous_status?: ApprovalStatus;
    new_status?: ApprovalStatus;
    mentioned_users?: string[];
  };
}

/**
 * Workflow spot notes with conversation history
 */
export interface WorkflowSpotNotes {
  content: string; // Main description/notes
  conversation_history: ConversationEntry[];
  last_updated: Timestamp;
  last_updated_by: string;
  last_updated_by_name: string;
  
  // Prompt questions for initial setup
  team_strengths?: string;
  growth_opportunities?: string;
  current_projects?: string;
  matching_considerations?: string;
}

// ============================================================================
// APPROVAL WORKFLOW
// ============================================================================

/**
 * Approval history entry
 */
export interface ApprovalHistoryEntry {
  timestamp: Timestamp;
  action: 'submitted' | 'approved' | 'archived' | 'unarchived';
  actor_id: string;
  actor_name: string;
  notes?: string;
}

/**
 * Approval metadata for workflow spots
 */
export interface ApprovalMetadata {
  submitted_at?: Timestamp;
  submitted_by?: string;
  submitted_by_name?: string;
  reviewed_at?: Timestamp;
  reviewed_by?: string;
  reviewed_by_name?: string;
  review_notes?: string;
  approval_history: ApprovalHistoryEntry[];
}

// ============================================================================
// WORKFLOW SPOT CONFIGURATION
// ============================================================================

/**
 * Entry tracking a previous confidence level value for audit history
 */
export interface WorkflowConfidenceHistoryEntry {
  previous_value: number;
  changed_at: Timestamp;
  changed_by: string;
}

/**
 * Workflow spot configuration
 */
export interface WorkflowSpotConfiguration {
  // Core configuration fields
  complexity: ComplexityLevel;
  team_experience: TeamExperience[];
  project_style_preference: ProjectStyle[];
  confidence_level: number; // 1-10
  confidence_history?: WorkflowConfidenceHistoryEntry[];
  decision_drivers: DecisionDriver[];

  // Notes and conversation
  notes: WorkflowSpotNotes;

  // Metadata
  configured_at: Timestamp;
  configured_by: string;
  configured_by_name: string;
}

// ============================================================================
// ENHANCED WORKFLOW ASSIGNMENT
// ============================================================================

/**
 * Enhanced workflow assignment that supports both legacy workflows and new workflow spots
 */
export interface EnhancedWorkflowAssignment {
  // Core workflow identification
  workflow_id: string;
  workflow_name: string;
  
  // Team/Program context
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  
  // Role assignments (TeamWorkflow compatibility)
  first_responder: string | null;
  production_support_1: string | null;
  production_support_2: string | null;
  strategy_pfm_support: string | null;
  
  // WorkflowSummary compatibility
  complexity_level: ComplexityLevel;
  order: number;
  
  // NEW: Workflow Spot Configuration (optional for backward compatibility)
  workflow_spot_config?: WorkflowSpotConfiguration;
  
  // Enhanced approval workflow
  approval_status: ApprovalStatus;
  approval_metadata?: ApprovalMetadata;
  
  // Existing workflow fields
  status: WorkflowAssignmentStatus;
  priority: WorkflowPriority;
  created_at: Timestamp;
  updated_at: Timestamp;
  created_by: string;
  created_by_name: string;
  last_modified_by: string;
  last_modified_by_name: string;
  
  // Optional fields
  estimated_hours?: number;
  actual_hours?: number;
  notes?: string; // Legacy notes field
  workflow_config_ref?: string;
  
  // Version control
  version: number;
  
  // Metadata
  metadata?: {
    description?: string;
    tags?: string[];
    is_workflow_spot?: boolean; // Flag to distinguish new workflow spots
  };
}

// ============================================================================
// CRUD OPERATION INTERFACES
// ============================================================================

/**
 * Data structure for creating new workflow spot
 */
export interface CreateWorkflowSpotData {
  workflow_name: string;
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  
  // Workflow spot configuration
  complexity: ComplexityLevel;
  team_experience: TeamExperience[];
  project_style_preference: ProjectStyle[];
  confidence_level: number;
  decision_drivers: DecisionDriver[];
  
  // Initial notes
  notes_content: string;
  team_strengths?: string;
  growth_opportunities?: string;
  current_projects?: string;
  matching_considerations?: string;
  
  // Role assignments (optional)
  first_responder?: string | null;
  production_support_1?: string | null;
  production_support_2?: string | null;
  strategy_pfm_support?: string | null;
  ideal_start_date?: string | null;
  
  // Optional metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

/**
 * Data structure for updating workflow spot
 */
export interface UpdateWorkflowSpotData {
  workflow_name?: string;
  
  // Configuration updates
  complexity?: ComplexityLevel;
  team_experience?: TeamExperience[];
  project_style_preference?: ProjectStyle[];
  confidence_level?: number;
  decision_drivers?: DecisionDriver[];
  
  // Role assignments
  first_responder?: string | null;
  production_support_1?: string | null;
  production_support_2?: string | null;
  strategy_pfm_support?: string | null;
  ideal_start_date?: string | null;
  
  // Asana integration
  asana_task_gid?: string | null;
  
  // Earmarked status for sales resourcing
  earmarked_status?: boolean;
  earmarked_text?: string | null;
  
  // Status updates
  status?: WorkflowAssignmentStatus;
  priority?: WorkflowPriority;
  
  // Optional fields
  estimated_hours?: number;
  actual_hours?: number;
  notes?: string;
  
  // Metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

/**
 * Data structure for adding conversation entry
 */
export interface AddConversationEntryData {
  message: string;
  message_type: ConversationMessageType;
  metadata?: {
    previous_status?: ApprovalStatus;
    new_status?: ApprovalStatus;
    mentioned_users?: string[];
  };
}

// ============================================================================
// VALIDATION SCHEMAS
// ============================================================================

const ComplexityLevelSchema = z.enum(['high', 'medium', 'low']);
const TeamExperienceSchema = z.enum(['Mixed', 'Social', 'Search', 'Programmatic']);
const ProjectStyleSchema = z.enum(['Ecommerce', 'B2B Lead Gen', 'User Acquisition', 'Any']);
const DecisionDriverSchema = z.enum(['New Hire', 'Project Churn', 'Change in Workflow', 'Capacity Identification']);
const ApprovalStatusSchema = z.enum(['draft', 'pending_approval', 'approved', 'deferred']);
const ConversationMessageTypeSchema = z.enum(['comment', 'status_change', 'approval_request']);





export const CreateWorkflowSpotDataSchema = z.object({
  workflow_name: z.string().min(1).max(200),
  team_id: z.string().min(1),
  team_name: z.string().min(1),
  program_id: z.string().min(1),
  program_name: z.string().min(1),
  complexity: ComplexityLevelSchema,
  team_experience: z.array(TeamExperienceSchema).min(1),
  project_style_preference: z.array(ProjectStyleSchema).min(1),
  confidence_level: z.number().min(1).max(10),
  decision_drivers: z.array(DecisionDriverSchema).min(1),
  notes_content: z.string().max(5000),
  team_strengths: z.string().max(1000).optional(),
  growth_opportunities: z.string().max(1000).optional(),
  current_projects: z.string().max(1000).optional(),
  matching_considerations: z.string().max(1000).optional(),
  first_responder: z.string().nullable().optional(),
  production_support_1: z.string().nullable().optional(),
  production_support_2: z.string().nullable().optional(),
  strategy_pfm_support: z.string().nullable().optional(),
  ideal_start_date: z.string().nullable().optional(),
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).optional(),
  }).optional(),
});

export const UpdateWorkflowSpotDataSchema = z.object({
  workflow_name: z.string().min(1).max(200).optional(),
  complexity: ComplexityLevelSchema.optional(),
  team_experience: z.array(TeamExperienceSchema).min(1).optional(),
  project_style_preference: z.array(ProjectStyleSchema).min(1).optional(),
  confidence_level: z.number().min(1).max(10).optional(),
  decision_drivers: z.array(DecisionDriverSchema).min(1).optional(),
  first_responder: z.string().nullable().optional(),
  production_support_1: z.string().nullable().optional(),
  production_support_2: z.string().nullable().optional(),
  strategy_pfm_support: z.string().nullable().optional(),
  ideal_start_date: z.string().nullable().optional(),
  asana_task_gid: z.string().nullable().optional(),
  earmarked_status: z.boolean().optional(),
  earmarked_text: z.string().max(200).nullable().optional(),
  status: z.enum(['active', 'planning', 'completed', 'on_hold', 'cancelled']).optional(),
  priority: z.enum(['high', 'medium', 'low']).optional(),
  estimated_hours: z.number().min(0).optional(),
  actual_hours: z.number().min(0).optional(),
  notes: z.string().max(1000).optional(),
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).optional(),
  }).optional(),
});

export const AddConversationEntryDataSchema = z.object({
  message: z.string().min(1).max(2000),
  message_type: ConversationMessageTypeSchema,
  metadata: z.object({
    previous_status: ApprovalStatusSchema.optional(),
    new_status: ApprovalStatusSchema.optional(),
    mentioned_users: z.array(z.string()).optional(),
  }).optional(),
});

